import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { trackEvent } from "@/lib/analytics";
import Seo, { breadcrumbLd } from "@/components/seo/Seo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  { icon: "🚗", title: "All-Season Tires", desc: "Dependable everyday tire options for daily driving and family travel." },
  { icon: "🏁", title: "Performance Tires", desc: "Built for stronger handling, response, and highway driving performance." },
  { icon: "🛞", title: "Touring Tires", desc: "Designed for comfort, smooth ride quality, and longer mileage." },
  { icon: "🚙", title: "Truck & SUV Tires", desc: "Options for heavier vehicles, highway terrain, and utility needs." },
  { icon: "🌦️", title: "Climate Tires", desc: "Great for changing road conditions and year-round confidence." },
  { icon: "🛣️", title: "Highway Tires", desc: "Good for commuters, long drives, and consistent road travel." },
  { icon: "🚚", title: "Fleet Tires", desc: "Reliable tire options for business vehicles and service fleets." },
  { icon: "🔧", title: "Installation Services", desc: "Mounting, balancing, inspection, and professional installation." },
];

const sampleTires = [
  {
    name: "GT Radial Max Climate",
    type: "All-Season Tire",
    price: "$568.97",
    features: ["Great everyday value option", "All-season road performance", "Quote subject to size and availability"],
  },
  {
    name: "Kumho 3 Peak Climate",
    type: "All-Season Tire",
    price: "$658.82",
    features: ["60,000 mile warranty option", "Climate-focused tire choice", "Good balance of value and coverage"],
  },
  {
    name: "Continental High Performance",
    type: "70K Mile Warranty",
    price: "$816.74",
    features: ["High-performance tire option", "70,000 mile warranty", "Premium road handling"],
  },
];

const brands = [
  { name: "GT Radial", desc: "Affordable all-season options." },
  { name: "Kumho", desc: "Strong value and warranty choices." },
  { name: "Continental", desc: "Premium performance and touring tires." },
  { name: "Michelin", desc: "Comfort, quality, and long-life options." },
  { name: "Goodyear", desc: "Trusted tire options for many vehicles." },
  { name: "Bridgestone", desc: "Touring, performance, and SUV tires." },
  { name: "Firestone", desc: "Dependable tire choices for daily driving." },
  { name: "Hankook", desc: "Balanced price and performance." },
];

const processSteps = [
  { num: 1, title: "Search", desc: "Enter your vehicle, tire size, or plate information." },
  { num: 2, title: "Choose", desc: "Select your tire brand, mileage warranty, and tire type." },
  { num: 3, title: "Confirm", desc: "Our team confirms fitment, pricing, taxes, and availability." },
  { num: 4, title: "Install", desc: "We order your tires and schedule your installation appointment." },
];

const initialQuote = {
  fullName: "",
  phone: "",
  email: "",
  vehicle: "",
  tireSize: "",
  tirePreference: "All-Season Tires",
  message: "",
  // search context
  searchMode: "vehicle",
  year: "",
  make: "",
  model: "",
  trim: "",
  width: "",
  aspectRatio: "",
  rimSize: "",
  quantity: "4 Tires",
  state: "Alabama",
  plateNumber: "",
};

export default function Tires() {
  const [searchTab, setSearchTab] = useState("vehicle");
  const [form, setForm] = useState(initialQuote);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const requestThisTire = (tireName) => {
    set("message", `Interested in: ${tireName}. ${form.message ? form.message : ""}`.trim());
    document.getElementById("quote")?.scrollIntoView({ behavior: "smooth" });
  };

  const submitQuote = async (e) => {
    e?.preventDefault?.();
    if (!form.fullName.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        full_name: form.fullName.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        vehicle: form.vehicle.trim(),
        tire_size: form.tireSize.trim(),
        tire_preference: form.tirePreference,
        quantity: form.quantity,
        message: form.message.trim(),
        search_mode: searchTab,
        search_context: {
          year: form.year,
          make: form.make,
          model: form.model,
          trim: form.trim,
          width: form.width,
          aspect_ratio: form.aspectRatio,
          rim_size: form.rimSize,
          state: form.state,
          plate_number: form.plateNumber,
        },
      };
      await axios.post(`${API}/tire-quote`, payload);
      trackEvent("Lead", {
        content_type: "tire_quote",
        content_name: form.tirePreference || "tires",
        content_id: form.tireSize || form.vehicle || "tires",
        currency: "USD",
        value: 0,
      });
      toast.success("Tire quote request received! We'll reach out to confirm.");
      setSubmitted(true);
      setForm(initialQuote);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not submit quote. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="abc-app">
      <Seo
        path="/tires"
        title="Shop Tires in Birmingham AL — Auto Basic Care"
        description="Find the right tires for your car, truck, SUV, or fleet vehicle. Request a quote, place an order, and schedule professional installation at Auto Basic Care in Birmingham, AL."
        jsonLd={breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Tires", path: "/tires" },
        ])}
      />
      <Toaster position="top-center" richColors />
      <SiteHeader />

      <section className="tires-hero">
        <div className="container tires-hero-grid">
          <div>
            <div className="badge">Good Prices. Good Name. Reliable Service.</div>
            <h1>Shop Tires with Auto Basic Care</h1>
            <p>
              Find the right tires for your car, truck, SUV, or fleet vehicle. Request a tire quote,
              place an order, and schedule professional installation with Auto Basic Care.
            </p>
            <div className="button-row">
              <a href="#shop" className="btn btn-primary" data-testid="tires-shop-btn">Shop by Vehicle</a>
              <a href="#quote" className="btn btn-outline-light" data-testid="tires-quote-btn">Request Tire Quote</a>
              <a href="tel:2054051238" className="btn btn-outline-light" data-testid="tires-call-btn">Call Now</a>
            </div>
          </div>

          <aside className="tires-hero-card">
            <h3>Need Tires This Week?</h3>
            <p className="muted">We help customers choose the right tire, order it, and schedule installation.</p>
            <ul>
              <li>All-season, climate, performance, and fleet tires</li>
              <li>40,000–70,000 mile warranty options available</li>
              <li>Mounting, balancing, inspection, and installation</li>
              <li>Appointment scheduling after tire arrival</li>
            </ul>
            <a href="#quote" className="btn btn-dark" data-testid="tires-start-order">Start Tire Order</a>
          </aside>
        </div>
      </section>

      <main>
        <div className="container" id="shop">
          <div className="tire-search-box">
            <div className="section-header" style={{ marginBottom: 18 }}>
              <div>
                <div className="eyebrow">Find your fit</div>
                <h2 className="section-title" style={{ fontSize: "clamp(24px, 3vw, 36px)" }}>Find Your Tires</h2>
              </div>
            </div>
            <p className="muted" style={{ marginBottom: 18 }}>
              Search by vehicle, tire size, or license plate. Our team will confirm fitment before ordering.
            </p>

            <div className="tire-tabs" role="tablist">
              {[
                ["vehicle", "Shop by Vehicle"],
                ["size", "Shop by Tire Size"],
                ["plate", "Shop by Plate Number"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  className={`tire-tab-btn ${searchTab === key ? "active" : ""}`}
                  onClick={() => setSearchTab(key)}
                  data-testid={`tire-tab-${key}`}
                >
                  {label}
                </button>
              ))}
            </div>

            {searchTab === "vehicle" && (
              <div className="tire-form-grid">
                <div><label>Year<input value={form.year} onChange={(e) => set("year", e.target.value)} placeholder="2019" data-testid="tire-year" /></label></div>
                <div><label>Make<input value={form.make} onChange={(e) => set("make", e.target.value)} placeholder="Nissan" data-testid="tire-make" /></label></div>
                <div><label>Model<input value={form.model} onChange={(e) => set("model", e.target.value)} placeholder="Kicks" data-testid="tire-model" /></label></div>
                <div><label>Trim<input value={form.trim} onChange={(e) => set("trim", e.target.value)} placeholder="SV / SR / S" data-testid="tire-trim" /></label></div>
              </div>
            )}
            {searchTab === "size" && (
              <div className="tire-form-grid">
                <div><label>Width<input value={form.width} onChange={(e) => set("width", e.target.value)} placeholder="205" data-testid="tire-width" /></label></div>
                <div><label>Aspect Ratio<input value={form.aspectRatio} onChange={(e) => set("aspectRatio", e.target.value)} placeholder="55" data-testid="tire-aspect" /></label></div>
                <div><label>Rim Size<input value={form.rimSize} onChange={(e) => set("rimSize", e.target.value)} placeholder="16" data-testid="tire-rim" /></label></div>
                <div>
                  <label>Quantity
                    <select value={form.quantity} onChange={(e) => set("quantity", e.target.value)} data-testid="tire-qty">
                      <option>1 Tire</option>
                      <option>2 Tires</option>
                      <option>4 Tires</option>
                    </select>
                  </label>
                </div>
              </div>
            )}
            {searchTab === "plate" && (
              <div className="tire-form-grid">
                <div>
                  <label>State
                    <select value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="tire-state">
                      <option>Alabama</option>
                      <option>Georgia</option>
                      <option>Florida</option>
                      <option>Mississippi</option>
                      <option>Tennessee</option>
                    </select>
                  </label>
                </div>
                <div><label>License Plate Number<input value={form.plateNumber} onChange={(e) => set("plateNumber", e.target.value)} placeholder="ABC1234" data-testid="tire-plate" /></label></div>
                <div><label>Phone Number<input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="205-405-1238" data-testid="tire-plate-phone" /></label></div>
                <div><label>Email<input value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="customer@email.com" data-testid="tire-plate-email" /></label></div>
              </div>
            )}

            <div style={{ marginTop: 18, display: "flex", gap: 12, flexWrap: "wrap" }}>
              <a href="#quote" className="btn btn-primary" data-testid="tire-check-options">Check Tire Options</a>
              <a href="#quote" className="btn btn-dark" data-testid="tire-request-quote">Request Quote</a>
            </div>
          </div>
        </div>

        <section className="section">
          <div className="container">
            <div className="section-header">
              <div>
                <div className="eyebrow">Tire Categories</div>
                <h2 className="section-title">Choose the right tire for how you drive.</h2>
              </div>
            </div>
            <div className="tire-cat-grid">
              {categories.map((c) => (
                <div key={c.title} className="tire-cat-card">
                  <div className="icon">{c.icon}</div>
                  <h3>{c.title}</h3>
                  <p>{c.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section" style={{ background: "var(--gray-soft)" }} id="brands">
          <div className="container">
            <div className="section-header">
              <div>
                <div className="eyebrow">Popular Options</div>
                <h2 className="section-title">Sample tire quotes to get you started.</h2>
              </div>
            </div>
            <div className="tire-product-grid">
              {sampleTires.map((t) => (
                <div key={t.name} className="tire-product">
                  <div className="tire-product-top">
                    <h3>{t.name}</h3>
                    <small>{t.type}</small>
                    <div className="price">{t.price}</div>
                  </div>
                  <div className="tire-product-body">
                    <ul>
                      {t.features.map((f) => <li key={f}>{f}</li>)}
                    </ul>
                    <button
                      className="btn btn-dark"
                      onClick={() => requestThisTire(t.name)}
                      data-testid={`request-tire-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      Request This Tire
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="brand-grid-tires" style={{ marginTop: 28 }}>
              {brands.map((b) => (
                <div key={b.name} className="brand-card">
                  <h3>{b.name}</h3>
                  <p>{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section">
          <div className="container">
            <div className="section-header">
              <div>
                <div className="eyebrow">How It Works</div>
                <h2 className="section-title">From search to installation, simple.</h2>
              </div>
            </div>
            <div className="process-grid">
              {processSteps.map((s) => (
                <div key={s.num} className="process-card">
                  <div className="num">{s.num}</div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="warranty-band" id="warranty">
          <div className="container">
            <div className="warranty-band-inner">
              <div>
                <h2>Tire Warranty Coverage</h2>
                <p style={{ color: "#d4d4d8" }}>
                  Select tire purchases may include manufacturer-backed mileage warranties ranging
                  from 40,000 to 70,000 miles depending on tire brand and model.
                </p>
                <a href="#quote" className="btn btn-primary" style={{ marginTop: 20 }} data-testid="warranty-ask-btn">
                  Ask About Warranty
                </a>
              </div>
              <ul>
                <li>Tires must be fully inspected before warranty approval.</li>
                <li>Tread depth, wear pattern, alignment, suspension, and rotation history may be reviewed.</li>
                <li>Uneven wear from alignment, suspension, improper inflation, or lack of maintenance may affect eligibility.</li>
                <li>Customer is responsible for labor, mounting, balancing, valve stems, disposal, taxes, and service charges unless otherwise stated.</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="tire-quote" id="quote">
          <div className="container tire-quote-grid">
            <aside className="tire-quote-info">
              <h2>Request a Tire Quote</h2>
              <p>Tell us what you need and our team will help confirm the best tire options for your vehicle.</p>
              <p><strong>Call:</strong> 205-405-1238</p>
              <p><strong>Location:</strong> 1700 31st Street SW, Birmingham, AL 35221</p>
              <p><strong>Brand Promise:</strong> Good Prices. Good Name. Reliable Service.</p>
            </aside>

            <form className="tire-quote-form" onSubmit={submitQuote} data-testid="tire-quote-form">
              {submitted ? (
                <div style={{ textAlign: "center", padding: "30px 10px" }}>
                  <div className="success-icon" style={{ margin: "0 auto 16px" }}>✓</div>
                  <h2 style={{ fontSize: 28, letterSpacing: "-0.04em", margin: "0 0 10px", color: "var(--dark)" }}>Quote Request Received</h2>
                  <p className="muted">Auto Basic Care will contact you to confirm pricing, availability, and scheduling.</p>
                  <button type="button" className="btn btn-dark" style={{ marginTop: 20 }} onClick={() => setSubmitted(false)} data-testid="tire-quote-another">
                    Submit Another Quote
                  </button>
                </div>
              ) : (
                <>
                  <div className="two-col">
                    <label>Full Name<input required value={form.fullName} onChange={(e) => set("fullName", e.target.value)} placeholder="Your name" data-testid="quote-name" /></label>
                    <label>Phone Number<input required type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="205-405-1238" data-testid="quote-phone" /></label>
                    <label>Email<input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" data-testid="quote-email" /></label>
                    <label>Vehicle<input value={form.vehicle} onChange={(e) => set("vehicle", e.target.value)} placeholder="Year Make Model" data-testid="quote-vehicle" /></label>
                    <label>Tire Size<input value={form.tireSize} onChange={(e) => set("tireSize", e.target.value)} placeholder="Example: 205/55R16" data-testid="quote-size" /></label>
                    <label>Tire Preference
                      <select value={form.tirePreference} onChange={(e) => set("tirePreference", e.target.value)} data-testid="quote-preference">
                        <option>All-Season Tires</option>
                        <option>Performance Tires</option>
                        <option>Touring Tires</option>
                        <option>Truck/SUV Tires</option>
                        <option>Climate Tires</option>
                        <option>Best Available Price</option>
                      </select>
                    </label>
                    <label className="full">Additional Details
                      <textarea value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us your tire brand preference, warranty needs, or installation timing." data-testid="quote-message" />
                    </label>
                  </div>
                  <div style={{ marginTop: 16 }}>
                    <button className="btn btn-primary" type="submit" disabled={submitting} data-testid="quote-submit">
                      {submitting ? "Submitting..." : "Submit Tire Quote Request"}
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </section>

        <section className="tires-cta">
          <div className="container">
            <h2>Ready for New Tires?</h2>
            <p>Auto Basic Care makes tire buying simple. Search your tire size, request a quote, and schedule your installation with our team.</p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <a href="#quote" className="btn btn-dark" data-testid="tires-cta-quote">Request Tire Quote</a>
              <a href="tel:2054051238" className="btn btn-outline-light" data-testid="tires-cta-call">Call 205-405-1238</a>
              <Link to="/faq" className="btn btn-outline-light" data-testid="tires-cta-faq">Tire FAQ</Link>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
